/**
 * Backend pul qiymatlarini `Decimal` sifatida saqlaydi (CLAUDE.md §3.1) va
 * Pydantic ularni JSON'ga **satr** ko'rinishida yozadi: `"252000.00"`.
 * Frontend tiplarida esa bu maydonlar `number` deb e'lon qilingan.
 *
 * Ko'rsatishda muammo sezilmaydi (`formatMoney` satrni ham hazm qiladi), lekin
 * **arifmetikada** jimgina buziladi:
 *
 *     stores.reduce((sum, s) => sum + s.debt_balance, 0)
 *     // 0 + "252000.00" + "200000.00" -> "0252000.00200000.00" -> NaN
 *
 * Shu sabab "Do'konlar va qarzlar" ekranida JAMI QARZ doim `0 so'm` chiqardi
 * (BUGS.md BUG-012). Bir joyda tuzatamiz: javob tanasidagi o'nlik ko'rinishdagi
 * satrlar songa aylantiriladi.
 *
 * Ehtiyot choralari:
 *  - faqat **nuqtali** o'nlik satrlar (`"4000.00"`) o'giriladi. Nuqtasiz
 *    `"12345"` tegilmaydi — kod/identifikator bo'lishi mumkin.
 *  - `+998...`, `ORD-...`, `2026-09-05`, ISO sana-vaqt — shablonga tushmaydi.
 *  - `DENY_KEYS` dagi maydonlar hech qachon o'girilmaydi.
 *  - rekursiya chuqurligi cheklangan (aylanma havoladan himoya).
 */

/** `"4000.00"`, `"-12.5"`, `"0.000"` — lekin `"4000"` emas. */
const DECIMAL_RE = /^-?\d{1,15}\.\d{1,6}$/;

/**
 * ISO sana-vaqt (masalan "2026-09-11T13:26:00" yoki "2026-09-11 13:26:00.123456").
 * Backend barcha sana-vaqtlarni UTC da saqlaydi, lekin bazadan o'qilganda timezone
 * belgisi (Z) tushib qolishi mumkin. Brauzer buni xato qilib lokal vaqt deb hisoblamasligi
 * uchun 'Z' qo'shamiz.
 * Eslatma: faqat sana bo'lgan satrlar ("2026-09-06") va allaqachon Z yoki offsetga ega
 * bo'lganlar tegilmaydi.
 */
const NAIVE_ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(\.\d+)?$/;

/** Son bo'lib ko'rinsa ham satrligicha qolishi shart bo'lgan maydonlar. */
const DENY_KEYS = new Set(['version', 'app_version', 'order_no', 'phone']);

const MAX_DEPTH = 12;

function convert(value: unknown, key: string | null, depth: number): unknown {
  if (depth > MAX_DEPTH) return value;

  if (typeof value === 'string') {
    if (key !== null && DENY_KEYS.has(key)) return value;
    if (DECIMAL_RE.test(value)) return Number(value);
    if (NAIVE_ISO_DATETIME_RE.test(value)) {
      return value.replace(' ', 'T') + 'Z';
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((v) => convert(v, key, depth + 1));
  }

  if (value !== null && typeof value === 'object') {
    const src = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(src)) {
      out[k] = convert(src[k], k, depth + 1);
    }
    return out;
  }

  return value;
}

/** API javobidagi o'nlik satrlarni songa aylantiradi (rekursiv, nusxa qaytaradi). */
export function normalizeDecimals<T>(body: T): T {
  return convert(body, null, 0) as T;
}
